import { ArrowLeft } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

/**
 * Terms of Use — verbatim rendering of the operator-approved terms
 * document. Bump CURRENT_TERMS_VERSION on the backend when this text
 * changes materially so returning users are re-stamped with fresh
 * consent.
 */
export default function Terms() {
  const today = new Date().toISOString().slice(0, 10);
  const navigate = useNavigate();
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/login");
  };
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <button
          type="button"
          onClick={goBack}
          data-testid="terms-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">
          Terms of Use
        </h1>
        <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-dim)]">
          v2.2 · {today}
        </span>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-12">
        <section>
          <h2 className="font-prose text-4xl italic leading-tight text-[var(--text)] mb-4">
            Read this before you rely on anything you read here.
          </h2>
          <p className="font-ui text-base text-[var(--text-muted)] leading-relaxed">
            Humane Behavior AI is a research and reflection tool. It is
            <strong> not </strong>a substitute for a doctor, therapist,
            lawyer, financial advisor, or any other licensed professional.
            You accept these terms by ticking the box at sign-up. If you
            are browsing without an account, you accept them by using the
            site. If you don&apos;t agree with them, please close the tab.
            This service is in beta. Features change, and things break.
          </p>
        </section>

        <Block
          eyebrow="Who we are"
          title="Humane Behavior AI is operated as humanebehavior.com, based in Chennai, India."
          testid="terms-who-we-are"
        >
          <p>
            For any legal, privacy, or support matter, contact{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>
            . If the service is transferred to an incorporated entity,
            this section will be updated before the transfer takes effect
            and registered users will be notified.
          </p>
        </Block>

        <Block
          eyebrow="Who can use this"
          title="Adults only. 18 and over."
          testid="terms-who-can-use"
        >
          <p>
            You must be at least 18 to use this site or create an
            account. By using it you confirm that you are. This site is
            not intended for, directed to, or designed for minors. If we
            learn an account belongs to someone under 18, we close it
            and delete the associated data. If you are using the site on
            behalf of an organisation, you confirm you have authority to
            bind that organisation to these terms.
          </p>
        </Block>

        <Block
          eyebrow="What this is"
          title="A conversational research tool. Not advice, not diagnosis, not therapy."
          testid="terms-what-this-is"
        >
          <p>
            Humane Behavior AI generates text that explains observed
            human behavior in mechanism-first, non-therapeutic language.
            It draws on public research and internal case notes to
            produce plausible explanations. It does <strong>not</strong>{" "}
            provide medical, psychological, legal, financial, or safety
            advice.
          </p>
          <p>
            Using this site does not create a professional relationship
            of any kind — not doctor-patient, not therapist-client, not
            attorney-client, not adviser-client. No duty of care arises
            from any output, and nothing produced here is privileged or
            confidential in the legal sense. Any decision you make about
            your life, your relationships, your money, your health, or
            another person is your own.
          </p>
        </Block>

        <Block
          eyebrow="This tool is direct"
          title="Answers are unsoftened. Some of them will be uncomfortable to read."
          testid="terms-direct"
        >
          <p>
            Humane Behavior AI explains behaviour without reassurance,
            hedging, or bedside manner. It discusses adult subject
            matter — sexual behaviour, violence, addiction, coercion,
            abuse — analytically and in clinical terms. It does not
            produce sexually explicit material.
          </p>
          <p>
            Answers may be blunt about you, about people you describe,
            or about situations you are in. That is the design, not a
            malfunction. If you want support rather than analysis, this
            is the wrong tool, and we would rather you knew that before
            you started than after.
          </p>
        </Block>

        <Block
          eyebrow="If you are in crisis"
          title="There is no human watching. Please contact someone who can help."
          testid="terms-crisis"
        >
          <p>
            If you are in immediate danger, contact your local emergency
            services or a crisis line. In India, <strong>Tele-MANAS</strong>{" "}
            is free and available 24/7 on <strong>14416</strong>.
          </p>
          <p>
            The model is designed to recognise signals of crisis or
            acute distress, change its register, and point you toward
            help. That is generated text, not a human intervention. We
            do not monitor conversations in real time, we do not alert
            anyone on your behalf, and we do not contact emergency
            services for you. Do not rely on this site in an emergency.
          </p>
          <p>
            Conversations classified as crisis-related are never stored
            in Memory, never used for training, and cannot be shared
            publicly. See the{" "}
            <Link
              to="/privacy"
              className="text-[var(--accent)] hover:underline"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </Block>

        <Block
          eyebrow="AI-generated content — disclosure"
          title="Every assistant reply is marked as AI-generated (EU AI Act Article 50)."
          testid="terms-ai-marking"
        >
          <p>
            To comply with the EU AI Act (Article 50) and equivalent
            transparency rules elsewhere, every reply from the assistant
            is clearly identified as AI-generated content:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>
              Live chat responses carry a machine-readable invisible marker
              at the start of the stream.
            </li>
            <li>
              PDF and DOCX exports include an &ldquo;AI-generated content&rdquo; banner
              on the first page and metadata (author, keywords) that flags
              the file as AI output for automated detectors.
            </li>
            <li>
              CSV exports have an <code>ai_generated</code> column that is
              <code>true</code> on every assistant row and <code>false</code>
              on every user row.
            </li>
            <li>
              Public share pages (<code>/s/…</code>) include a visible
              banner and machine-readable HTML meta tags marking the reply
              as AI-generated.
            </li>
          </ul>
          <p className="mt-2">
            You must not remove, obscure, or falsify these markers when
            reposting or exporting content you obtained from this service.
          </p>
        </Block>

        <Block
          eyebrow="AI can be wrong"
          title="Every answer can contain mistakes, guesses, or invented details."
          testid="terms-ai-wrong"
        >
          <p>
            The model behind this site is a probabilistic language
            model. It can produce answers that sound confident and are
            factually wrong, ethically questionable, or simply invented.
            Names, citations, statistics, and quotes may not correspond
            to anything real. <strong>Always verify anything that
            matters</strong> before you act on it, share it, or
            attribute it to a person.
          </p>
          <p>
            The site does not remember prior conversations unless you
            enable Memory, and even then only narrow, durable facts are
            kept. Do not assume the AI knows the current date, the
            current state of the world, or anything that happened after
            its training cutoff.
          </p>
        </Block>

        <Block
          eyebrow="Outputs about other people"
          title="Anything the model says about a real person is a hypothesis, not a finding."
          testid="terms-third-parties"
        >
          <p>
            If you describe someone — a partner, a colleague, a family
            member — the model will produce explanations of their
            behaviour. Those explanations are generated from your
            account of events. The model has not met them, has no
            independent information about them, and cannot know whether
            what you have described is accurate or complete.
          </p>
          <p>
            Nothing the model produces about an identifiable person is a
            statement of fact about that person. It is not a diagnosis,
            not an assessment, not evidence, and not a professional
            opinion. Treating it as one — or repeating it to others as
            one — is a misuse of this site and may expose you to legal
            liability of your own.
          </p>
        </Block>

        <Block
          eyebrow="No reliance"
          title="Use it for thinking. Don't use it as the last word."
          testid="terms-no-reliance"
        >
          <p>
            Nothing produced on this site is a professional opinion.
            Answers are for personal reflection and research only. You
            agree not to rely on any output for medical treatment, legal
            strategy, investment decisions, safety-critical choices, or
            evaluating another person&apos;s mental health.
          </p>
        </Block>

        <Block
          eyebrow="Acceptable use"
          title="Don't use the site to harm people, break laws, or deceive anyone."
          testid="terms-acceptable-use"
        >
          <p>You agree not to use the site, or its outputs, to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Diagnose, treat, or evaluate the mental health of a
              specific person without their knowledge and consent.
            </li>
            <li>
              Make or inform decisions about another person&apos;s
              employment, promotion, dismissal, education, credit,
              insurance, housing, or access to services. This tool is
              not built or validated for those purposes and must not be
              used for them.
            </li>
            <li>
              Infer or assess a person&apos;s emotional state in a
              workplace or educational setting.
            </li>
            <li>
              Assess a person&apos;s likelihood of committing an
              offence, or profile anyone for law-enforcement purposes.
            </li>
            <li>
              Generate content that harasses, defames, or discriminates
              against any individual or group.
            </li>
            <li>
              Produce or solicit sexual content involving minors, or any
              content that sexualises a minor, in any form or framing.
            </li>
            <li>
              Impersonate Humane Behavior AI or present its output as a
              licensed professional opinion.
            </li>
            <li>
              Scrape, mirror, or resell outputs at scale, or use them to
              train, fine-tune, or evaluate competing models.
            </li>
            <li>
              Break any applicable law, or violate anyone&apos;s privacy
              or intellectual property.
            </li>
          </ul>
          <p>
            We may suspend or terminate an account for violations. Where
            practicable we tell you first and give you a chance to
            respond; where the violation is serious or ongoing we may
            act immediately and explain afterwards. You can appeal any
            suspension by writing to{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>
            .
          </p>
        </Block>

        <Block
          eyebrow="Your account"
          title="You're responsible for what happens under your login."
          testid="terms-account"
        >
          <p>
            Keep your password and any linked Google account secure.
            Activity under your account is your responsibility unless it
            results from a failure on our side. Notify us at{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>{" "}
            if you suspect unauthorized access, and we will act on it
            immediately.
          </p>
          <p>
            You can close your account at any time from settings or by
            writing to us. On closure you can export your data first;
            see the{" "}
            <Link
              to="/privacy"
              className="text-[var(--accent)] hover:underline"
            >
              Privacy Policy
            </Link>{" "}
            for what is deleted and when.
          </p>
        </Block>

        <Block
          eyebrow="Intellectual property"
          title="You own your inputs. We own the site."
          testid="terms-ip"
        >
          <p>
            You retain ownership of the text, files, and prompts you
            submit. We claim no ownership of them and no licence beyond
            what is needed to run the service and to honour any
            training-data consent you have separately given. The
            site&apos;s code, design, brand, and documentation are ours.
          </p>
          <p>
            As between you and us, outputs are yours. You can read them,
            share them, paraphrase them, cite them, and use them for your
            own purposes. You may not resell them as a commercial
            product, or use them to build a competing service, without
            written permission.
          </p>
          <p>
            <strong>Two honest limits.</strong> We cannot guarantee
            outputs are original — a generative model may produce text
            similar or identical to text it produces for someone else,
            or to text that already exists. And we make no
            representation that any output is free of third-party
            rights. If originality matters for your use, check before
            you publish. If you send us feedback or suggestions, we may
            use them without obligation to you.
          </p>
        </Block>

        <Block
          eyebrow='Provided "As Is" and Limitation of Liability'
          title='The site and its outputs are provided "as is." Our liability is limited.'
          testid="terms-liability"
        >
          <p>
            The site, service, and all AI-generated outputs are provided
            &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; To the
            maximum extent permitted by applicable law, we disclaim all
            warranties, express or implied, including warranties of
            merchantability, fitness for a particular purpose,
            non-infringement, accuracy, completeness, reliability,
            availability, and suitability. We do not guarantee that any
            AI-generated output will be accurate, complete, current,
            safe, or appropriate for any particular purpose, or that
            the service will operate without interruption or error.
          </p>
          <p>
            To the maximum extent permitted by applicable law, we will
            not be liable for any indirect, incidental, consequential,
            special, punitive, or exemplary damages arising from or
            related to your use of, or reliance on, the site, service,
            or AI-generated output. This includes, without limitation,
            loss of data, income, profits, opportunity, reputation, or
            relationships; emotional distress; or consequences arising
            from decisions or actions taken by you or any third party
            based on AI-generated output.
          </p>
          <p>
            Where liability cannot be excluded but may lawfully be
            limited, our total aggregate liability for all claims
            arising from or relating to the site or service will not
            exceed the amount you paid us for the service during the
            six months immediately preceding the event giving rise to
            the claim.
          </p>
          <p>
            For users who have not paid us for the service, we exclude
            monetary liability to the maximum extent permitted by
            applicable law.
          </p>
          <p>
            Nothing in these Terms excludes, restricts, or limits any
            liability that cannot lawfully be excluded, restricted, or
            limited under applicable law.
          </p>
        </Block>

        <Block
          eyebrow="What We Do Not Attempt to Exclude"
          title="Some liabilities cannot be waived by contract. We do not try to."
          testid="terms-non-waivable"
        >
          <p>
            Nothing in these Terms excludes or limits liability to the
            extent that it cannot lawfully be excluded or limited under
            applicable law. This includes, where applicable, liability
            for death or personal injury caused by negligence, fraud or
            fraudulent misrepresentation, gross negligence or wilful
            misconduct, and liability arising under any consumer
            protection or other law that cannot be waived by agreement.
          </p>
          <p>
            If any part of this section, or any limitation or exclusion
            of liability elsewhere in these Terms, is found to be
            unlawful or unenforceable in your jurisdiction, that
            provision will apply only to the maximum extent permitted
            by law. The remaining provisions will continue in effect.
          </p>
        </Block>

        <Block
          eyebrow="Indemnity"
          title="If you break the rules and someone comes after us because of it, you cover the costs."
          testid="terms-indemnity"
        >
          <p>
            If you use the site in breach of the Acceptable use section
            and a third party brings a claim against us because of it,
            you agree to cover the reasonable costs of defending that
            claim. This does not apply to claims arising from our own
            acts or omissions, and it does not apply where you are using
            the site as a consumer for personal purposes and the law
            where you live prevents it.
          </p>
        </Block>

        <Block
          eyebrow="Suspension and termination"
          title="You can stop using the site at any time."
          testid="terms-termination"
        >
          <p>
            We may suspend or end access for breach of these terms, for
            legal reasons, or if we discontinue the service. If we
            discontinue the service, we will give registered users
            reasonable notice and a window to export their data.
            Sections that by their nature should survive termination —
            intellectual property, liability, indemnity, governing law
            — do.
          </p>
        </Block>

        <Block
          eyebrow="Changes to these terms"
          title="We will tell you. Silence is not always agreement."
          testid="terms-changes"
        >
          <p>
            We may update these terms as the product evolves or the law
            changes. Material changes are flagged at the top of this
            page and notified in the site before they take effect. Where
            a change materially reduces your rights or materially
            changes what we do with your data, we ask you to accept it
            explicitly rather than inferring acceptance from continued
            use. For other changes, continuing to use the site after the
            effective date means you accept them. Either way you can
            close your account and take your data with you.
          </p>
        </Block>

        <Block
          eyebrow="If something goes wrong between us"
          title="Write first. Most things can be sorted this way."
          testid="terms-dispute"
        >
          <p>
            Before starting formal proceedings, please write to{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="text-[var(--accent)] hover:underline"
            >
              support@humanebehavior.com
            </a>{" "}
            describing the problem. We will respond within 30 days and
            try to resolve it directly. Most things can be sorted this
            way, and neither of us wants the alternative.
          </p>
        </Block>

        <Block
          eyebrow="Governing law"
          title="Indian law, with your local consumer rights intact."
          testid="terms-law"
        >
          <p>
            These terms are governed by the laws of India, without
            regard to conflict-of-laws principles. Subject to the
            paragraph below, disputes will be resolved in the competent
            courts of Tiruchirapalli, India.
          </p>
          <p>
            Nothing in this clause limits your non-waivable rights. If
            you are a consumer, you may bring proceedings in the courts
            where you live, and the mandatory consumer protection law of
            your own country continues to apply to you regardless of the
            choice of law above.
          </p>
        </Block>

        <Block
          eyebrow="The rest"
          title="Severability, no waiver, assignment, entire agreement."
          testid="terms-misc"
        >
          <p>
            <strong>Severability.</strong> If any provision is held
            unenforceable, the rest stands.
          </p>
          <p>
            <strong>No waiver.</strong> If we don&apos;t enforce
            something immediately, we haven&apos;t given up the right to
            enforce it later.
          </p>
          <p>
            <strong>Assignment.</strong> You may not assign these
            terms. We may assign them to a successor entity — including
            on incorporation — on notice to you.
          </p>
          <p>
            <strong>Entire agreement.</strong> These terms and the{" "}
            <Link
              to="/privacy"
              className="text-[var(--accent)] hover:underline"
            >
              Privacy Policy
            </Link>{" "}
            are the whole agreement between us about the site.
          </p>
        </Block>

        <section className="border-t border-[var(--border)] pt-8">
          <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Questions?{" "}
            <a
              href="mailto:support@humanebehavior.com"
              className="hover:text-[var(--accent)]"
            >
              support@humanebehavior.com
            </a>
            {" · "}
            <Link to="/privacy" className="hover:text-[var(--accent)]">
              Privacy Policy
            </Link>
          </p>
        </section>
      </main>
    </div>
  );
}

function Block({ eyebrow, title, children, testid }) {
  return (
    <section data-testid={testid}>
      <div className="font-mono-ui text-[10px] tracking-[0.32em] uppercase text-[var(--accent)] mb-3">
        {eyebrow}
      </div>
      <h2 className="font-prose text-2xl md:text-3xl text-[var(--text)] leading-tight mb-5">
        {title}
      </h2>
      <div className="space-y-4 font-ui text-[15px] text-[var(--text-muted)] leading-relaxed">
        {children}
      </div>
    </section>
  );
}
